import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { Modal } from "../../../../../components/common/Modal";
import { Button } from "../../../../../components/common/Button";
import { Input } from "../../../../../components/common/Input";
import { COLOR_SCHEMES } from "../../../../../components/common/DefaultAvatar/constants";

import type { RootState } from "../../../../../store";

import { AdminActionCreator } from "../../../../../store/Admin/actions";
import { UserActionCreator } from "../../../../../store/userActions";
import {
    // ✅ update user (admin)
    selectUpdateUserPending,
    selectUpdateUserError,
    selectUpdateUserErrorMessage,

    // ✅ подробный юзер/контакты (user)
    selectUserPending,
    selectUserError,
    selectUserErrorMessage,
    selectUserData,
    selectTelegram,
    selectWhatsapp,
    selectInstagram,
} from "../../../../../store/selectors";

type Payload = {
    password?: string;
    name?: string;
    color?: string;
    telegram?: string;
    whatsapp?: string;
    instagram?: string;
    phone?: number | string;
};

type OwnProps = {
    isOpen: boolean;
    user: any | null;
    onClose: () => void;
};

type StateProps = {
    updatePending: boolean;
    updateError: boolean;
    updateErrorMessage: string | null;

    userInfoPending: boolean;
    userInfoError: boolean;
    userInfoErrorMessage: string | null;
    userInfo: any | null;

    telegramFromStore: string | null;
    whatsappFromStore: string | null;
    instagramFromStore: string | null;
};

type DispatchProps = {
    // ⚠️ фактически у тебя вызывается getUserInfoById({id: user.id})
    // поэтому тип оставляем как any, чтобы TS не мешал
    getUserInfoById: (payload: { id: number | string }) => void;

    updateUserRequest: (payload: { userId: number | string; payload: Payload }) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const isValidHexColor = (v: string) =>
    /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(String(v).trim());

const isSameId = (a: any, b: any) => {
    if (a == null || b == null) return false;
    return String(a) === String(b);
};

const setIfEmpty = <T,>(current: any, setter: (v: T) => void, next: any) => {
    const cur = String(current ?? "").trim();
    const n = String(next ?? "").trim();
    if (!cur && n) setter(next as T);
};

const _EditUserModal: React.FC<Props> = ({
                                             isOpen,
                                             user,
                                             onClose,

                                             // from store
                                             updatePending,
                                             updateError,
                                             updateErrorMessage,

                                             userInfoPending,
                                             userInfoError,
                                             userInfoErrorMessage,
                                             userInfo,

                                             telegramFromStore,
                                             whatsappFromStore,
                                             instagramFromStore,

                                             // actions
                                             getUserInfoById,
                                             updateUserRequest,
                                         }) => {
    const userLabel = user?.email || user?.name || "пользователя";

    const [name, setName] = useState<string>("");
    const [color, setColor] = useState<string>("#111111");
    const [telegram, setTelegram] = useState<string>("");
    const [whatsapp, setWhatsapp] = useState<string>("");
    const [instagram, setInstagram] = useState<string>("");
    const [phone, setPhone] = useState<number | string>("");
    const [password, setPassword] = useState<string>("");

    // 1) при открытии — заполняем тем, что есть в строке таблицы
    useEffect(() => {
        if (!isOpen) return;

        setName(user?.name ?? "");
        setColor(user?.color ?? "#111111");
        setTelegram(user?.telegram ?? "");
        setWhatsapp(user?.whatsapp ?? "");
        setInstagram(user?.instagram ?? "");
        setPhone(user?.phone ?? "");
        setPassword("");
    }, [isOpen, user]);

    // 2) при открытии — тянем подробные данные
    useEffect(() => {
        if (!isOpen) return;
        if (!user?.id) return;

        // ✅ как было у тебя, но без лишнего console.log
        getUserInfoById({ id: user.id });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, user?.id]);

    // 3) когда пришла подробная инфа — дозаполняем пустые поля (не затираем ручной ввод)
    useEffect(() => {
        if (!isOpen) return;
        if (!user?.id) return;

        const infoId = userInfo?.id ?? null;
        if (infoId != null && !isSameId(infoId, user.id)) return;

        setIfEmpty<string>(name, setName, userInfo?.name);
        setIfEmpty<string>(color, setColor, userInfo?.color);

        if (!String(phone ?? "").trim() && userInfo?.phone != null) {
            setPhone(userInfo.phone);
        }

        setIfEmpty<string>(telegram, setTelegram, telegramFromStore);
        setIfEmpty<string>(whatsapp, setWhatsapp, whatsappFromStore);
        setIfEmpty<string>(instagram, setInstagram, instagramFromStore);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isOpen,
        user?.id,
        userInfo,
        telegramFromStore,
        whatsappFromStore,
        instagramFromStore,
    ]);

    const colorError = useMemo(() => {
        const v = String(color ?? "").trim();
        if (!v) return null;
        if (!isValidHexColor(v)) return "Некорректный hex-цвет";
        return null;
    }, [color]);

    const canSave = useMemo(() => {
        if (!user?.id) return false;
        if (updatePending) return false;
        if (colorError) return false;
        return true;
    }, [user?.id, updatePending, colorError]);

    const isActiveColor = (c: string) =>
        String(c).toLowerCase() === String(color).trim().toLowerCase();

    const handleConfirm = () => {
        if (!user?.id) return;

        // ✅ отправляем все поля, а password — только если ввели
        const payload: Payload = {
            name,
            // color,
            telegram,
            whatsapp,
            instagram,
            phone,
            ...(String(password ?? "").trim() ? { password } : {}),
        };

        updateUserRequest({
            userId: user.id,
            payload,
        });

        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={updatePending ? undefined : onClose}
            title="Редактировать пользователя"
        >
            <div className="text-sm text-slate-700">
                Пользователь: <span className="font-semibold">{userLabel}</span>
            </div>

            {userInfoPending ? (
                <div className="mt-2 text-xs text-slate-500">Загружаем подробные данные…</div>
            ) : null}

            {userInfoError ? (
                <div className="mt-2 text-xs text-red-500">
                    {userInfoErrorMessage || "Ошибка загрузки данных пользователя"}
                </div>
            ) : null}

            {updateError ? (
                <div className="mt-3 text-xs text-red-500">
                    {updateErrorMessage || "Ошибка сохранения"}
                </div>
            ) : null}

            <div className="mt-4 space-y-3">
                <Input
                    label="Имя"
                    name="name"
                    type="text"
                    placeholder="Напр. Бибулат Б."
                    value={name}
                    onChange={setName}
                    error={false}
                    errorMessage={null}
                    register={{}}
                />

                <Input
                    label="Телефон"
                    name="phone"
                    type="number"
                    placeholder="79991234567"
                    value={phone}
                    onChange={setPhone}
                    error={false}
                    errorMessage={null}
                    register={{}}
                />

                <Input
                    label="Telegram"
                    name="telegram"
                    type="text"
                    placeholder="@needle123"
                    value={telegram}
                    onChange={setTelegram}
                    error={false}
                    errorMessage={null}
                    register={{}}
                />

                <Input
                    label="WhatsApp"
                    name="whatsapp"
                    type="text"
                    placeholder="999123512"
                    value={whatsapp}
                    onChange={setWhatsapp}
                    error={false}
                    errorMessage={null}
                    register={{}}
                />

                <Input
                    label="Instagram"
                    name="instagram"
                    type="text"
                    placeholder="@mgmdk"
                    value={instagram}
                    onChange={setInstagram}
                    error={false}
                    errorMessage={null}
                    register={{}}
                />

                {/*<Input*/}
                {/*    label="Цвет (hex)"*/}
                {/*    name="color"*/}
                {/*    type="text"*/}
                {/*    placeholder="#111"*/}
                {/*    value={color}*/}
                {/*    onChange={setColor}*/}
                {/*    error={!!colorError}*/}
                {/*    errorMessage={colorError}*/}
                {/*    register={{}}*/}
                {/*/>*/}

                {/*<div className="mt-1">*/}
                {/*    <div className="text-xs font-semibold text-slate-600 mb-2">Быстрый выбор</div>*/}

                {/*    <div className="flex flex-wrap gap-2">*/}
                {/*        {(COLOR_SCHEMES ?? []).map((c) => {*/}
                {/*            const active = isActiveColor(c);*/}
                {/*            return (*/}
                {/*                <button*/}
                {/*                    key={c}*/}
                {/*                    type="button"*/}
                {/*                    onClick={() => setColor(c)}*/}
                {/*                    className={*/}
                {/*                        "h-7 w-7 rounded-full border transition " +*/}
                {/*                        (active*/}
                {/*                            ? "border-slate-900 ring-2 ring-slate-300"*/}
                {/*                            : "border-slate-200 hover:border-slate-400")*/}
                {/*                    }*/}
                {/*                    style={{ background: c }}*/}
                {/*                    title={c}*/}
                {/*                />*/}
                {/*            );*/}
                {/*        })}*/}
                {/*    </div>*/}

                {/*    <div className="mt-2 flex items-center gap-2 text-xs text-slate-600">*/}
                {/*        <span>Выбрано:</span>*/}
                {/*        <span className="font-semibold">{String(color || "").trim() || "—"}</span>*/}
                {/*        <span*/}
                {/*            className="h-4 w-4 rounded border border-slate-200"*/}
                {/*            style={{ background: isValidHexColor(color) ? color : "#fff" }}*/}
                {/*            title="preview"*/}
                {/*        />*/}
                {/*    </div>*/}
                {/*</div>*/}

                <Input
                    label="Новый пароль"
                    name="password"
                    type="password"
                    placeholder="Введите только если хотите изменить"
                    value={password}
                    onChange={setPassword}
                    error={false}
                    errorMessage={null}
                    register={{}}
                    autoComplete="new-password"
                />
            </div>

            <div className="mt-5 flex justify-end gap-2">
                <Button variant="secondary" onClick={onClose} text="Отмена" disabled={!!updatePending} />
                <Button
                    onClick={handleConfirm}
                    text={updatePending ? "Сохранение..." : "Сохранить"}
                    disabled={!canSave}
                />
            </div>
        </Modal>
    );
};

const mapStateToProps = (state: RootState) => ({
    // ✅ update profile (admin)
    updatePending: !!selectUpdateUserPending(state),
    updateError: !!selectUpdateUserError(state),
    updateErrorMessage: selectUpdateUserErrorMessage(state) ?? null,

    // ✅ detailed user info (user)
    userInfoPending: !!selectUserPending(state),
    userInfoError: !!selectUserError(state),
    userInfoErrorMessage: selectUserErrorMessage(state) ?? null,
    userInfo: selectUserData(state) ?? null,

    telegramFromStore: selectTelegram(state),
    whatsappFromStore: selectWhatsapp(state),
    instagramFromStore: selectInstagram(state),
});

const mapDispatchToProps = {
    getUserInfoById: UserActionCreator.getUserInfoById,
    updateUserRequest: AdminActionCreator.updateUser,
};

export const EditUserModal = connect(mapStateToProps, mapDispatchToProps)(_EditUserModal);

export default EditUserModal;
